import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, AnimatePresence } from "framer-motion";

// --- IMAGE IMPORTS ---
// Fixed to .png based on your file availability
import printingImg from '../assets/images/Gemini_Generated_Image_haeigkhaeigkhaei.png';
import roboticsImg from '../assets/images/Gemini_Generated_Image_2hw7vc2hw7vc2hw7.png';
import aiImg from '../assets/images/Gemini_Generated_Image_h9dpknh9dpknh9dp.png';
import droneImg from '../assets/images/Gemini_Generated_Image_jq6a80jq6a80jq6a.png';
import exhibitionImg from '../assets/images/productexpo.png';       
import ksumLogo from '../assets/KSUM.svg';
import landingImage from '../assets/landing.png';
import project from '../assets/images/services/d.png';
import consultation from '../assets/images/services/c.png';
import classes from '../assets/images/services/e.png';
import custom from '../assets/images/services/a.png';
import learningLogo from '../assets/images/learning_logo.png';

// --- FUTURISTIC ANIMATED BACKGROUND COMPONENT ---
function FuturisticBackground() {
  const { scrollY } = useScroll();

  // --- Parallax Effects ---
  const yGrid = useTransform(scrollY, [0, 1000], [0, -150]);
  const rotateGrid = useTransform(scrollY, [0, 1000], [0, 5]);
  
  // Gold orb removed per previous request, keeping only Orb2 (now Blue)
  const yOrb2 = useTransform(scrollY, [0, 1000], [0, -200]);
  
  // Rotating ring element
  const rotateRing = useTransform(scrollY, [0, 2000], [0, 360]);
  const smoothRotate = useSpring(rotateRing, { stiffness: 50, damping: 20 });

  return (
    <div className="fixed inset-0 overflow-hidden -z-10 bg-[#003049]">
      {/* 1. Deep Blue Gradient Base */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#001824] via-[#003049] to-[#004e75]" />

      {/* 2. Parallax Grid (Blue tinted) */}
      <motion.div 
        style={{ y: yGrid, rotateX: rotateGrid }}
        className="absolute inset-[-50%] w-[200%] h-[200%] origin-top bg-[linear-gradient(to_right,#457B9D22_1px,transparent_1px),linear-gradient(to_bottom,#457B9D22_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_110%)] perspective-1000" 
      />

      {/* 3. Steel Blue Energy Orb (Drifts up) */}
      <motion.div 
        style={{ y: yOrb2 }}
        className="absolute bottom-[10%] right-[5%] w-[40vw] h-[40vw] bg-[#457B9D] rounded-full blur-[140px] opacity-30 mix-blend-screen"
      />

      {/* 4. Center Tech Ring (Rotates on scroll) */}
      <motion.div
        style={{ rotate: smoothRotate }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#D1D1D1]/10 rounded-full border-dashed pointer-events-none"
      />

      {/* 5. Ambient Floating Particles */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-[#D1D1D1] rounded-full"
          initial={{
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
            y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000),
            scale: Math.random() * 2,
            opacity: Math.random() * 0.5 + 0.3
          }}
          animate={{
            y: [null, Math.random() * -150], // Float upwards
            opacity: [null, 0.1, 0.6, 0.1], // Twinkle
          }}
          transition={{
            duration: Math.random() * 15 + 15,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
      
      {/* 6. Top Light Flare */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-b from-[#457B9D]/40 to-transparent blur-[80px] pointer-events-none" />
    </div>
  );
}

// --- COUNT UP CARD COMPONENT ---
function CountUpCard({ label, end }) {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    let start = 0;
    const duration = 1200;
    const increment = end / (duration / 16);
    let raf;
    function animate() {
      start += increment;
      if (start < end) {
        setCount(Math.floor(start));
        raf = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    }
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [end]);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="bg-[#002639]/80 backdrop-blur-md rounded-xl shadow-lg p-8 flex flex-col items-center border border-[#457B9D] hover:shadow-[0_0_20px_rgba(209,209,209,0.3)] transition-all hover:-translate-y-1"
    >
      <div className="text-4xl font-bold text-[#D1D1D1] mb-2">{count}+</div>
      <div className="text-gray-300 font-medium">{label}</div>
    </motion.div>
  );
}

function LandingPage() {
  const [loading, setLoading] = useState(true);
  const [showBubble, setShowBubble] = useState(false);

  // bubble appears only when user taps the image
  const handleImageClick = () => {
    setShowBubble(prev => !prev);
  };

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#003049]">
        <div className="flex flex-col items-center relative">
          {/* Replaced loader with loading.io gooey SVG component */}
          <div className="w-40 h-40 mb-6 flex items-center justify-center">
            {/* loader will be injected by App-level component; keep simple fallback */}
            <div className="text-white">Loading…</div>
          </div>
        </div>
      </div>
    );  
  }

  const features = [
    { title: "Project Guidance and Mentorship" },
    { title: "Startup Consultation" },
    { title: "Education" },
    { title: "Design and Manufacturing" },
  ];

  return (
    <div className="font-sans bg-transparent min-h-screen text-gray-200 overflow-x-hidden relative selection:bg-[#D1D1D1] selection:text-[#003049]">
      <FuturisticBackground />
      
      {/* Hero Section */}
      <motion.main
        className="flex flex-col md:flex-row items-center justify-between px-6 md:px-16 py-12 md:py-20 gap-10 min-h-screen pt-20 relative z-10"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.6 }}
        transition={{ duration: 0.8 }}
      >
        
        {/* Left Section */}
        <div className="w-full max-w-screen-lg flex flex-col items-center md:items-start text-center md:text-left">
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight mb-8">
            <span className="text-white drop-shadow-md">Empowering Innovations</span> <br />
            <span className="text-[#D1D1D1] drop-shadow-[0_0_15px_rgba(209,209,209,0.5)]">Everywhere</span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 mb-10 max-w-2xl">
            Explore cutting-edge technology products and training
            courses. Join us to shape the future together.
          </p>
          <button
            className="bg-[#D1D1D1] text-[#003049] hover:bg-[#bfbfbf] px-8 py-4 rounded-md shadow-[0_4px_14px_0_rgba(209,209,209,0.39)] hover:shadow-[0_6px_20px_rgba(209,209,209,0.23)] transition-all duration-300 font-bold text-lg"
            onClick={() => window.location.href = "/about"}
          >
            Know more →
          </button>
        </div>


        {/* Right Section - Landing Image */}
        <div className="w-full md:w-1/2 flex justify-center md:justify-end items-end relative min-h-[400px]">
          {/* Animated Glow behind the image */}
            <motion.div
              className="absolute bottom-0 right-0 w-72 h-72 bg-[#457B9D] rounded-full blur-[120px] opacity-20"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{ duration: 5, repeat: Infinity }}
            />
            
            {/* Interactive Particles */}
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(5)].map((_, i) => (
                <motion.div
            key={i}
            className="absolute w-2 h-2 bg-[#D1D1D1] rounded-full"
            animate={{
              x: [Math.random() * 100 - 50, Math.random() * 300 - 150],
              y: [Math.random() * 100 - 50, Math.random() * 300 - 150],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: i * 0.5,
            }}
                />
              ))}
            </div>

            <motion.div
              onClick={handleImageClick}
              className="relative z-20 cursor-pointer md:translate-x-12 md:translate-y-12 group"
              initial={{ opacity: 0, x: 50, y: 50 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              whileHover={{ scale: 1.08, rotateY: 5 }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: true }}
              style={{ perspective: 1000 }}
            >
              {showBubble && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute top-0 left-0 -translate-y-full ml-2 w-48 bg-[#002639]/90 text-white rounded-lg p-3 shadow-lg"
                >
                  I'm Enzo, welcome you to Ente AI!
                </motion.div>
              )}
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-[#457B9D]/30 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300 -z-10"
                animate={{
            scale: [1, 1.2, 1],
                }}
                transition={{ duration: 4, repeat: Infinity }}
              />
              
              <motion.img
                src={landingImage}
                alt="Futuristic Robotics"
                className="w-full max-w-sm md:max-w-md lg:max-w-xl object-contain drop-shadow-[0_0_40px_rgba(209,209,209,0.3)] group-hover:drop-shadow-[0_0_60px_rgba(71,123,157,0.6)] transition-all duration-300"
                animate={{ 
            y: [0, -15, 0],
                }}
                transition={{ 
            y: { duration: 5, repeat: Infinity, ease: "easeInOut" }
                }}
                whileHover={{
            filter: "brightness(1.1)",
                }}
              />
            </motion.div>
          </div>
      </motion.main>

      {/* Learning Platform Banner */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-7xl mx-auto mb-16 px-6 relative z-10"
      >
        <div className="bg-gradient-to-r from-[#003049] to-[#457B9D] rounded-2xl p-8 md:p-12 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6 border border-[#457B9D] hover:border-[#D1D1D1] transition-all duration-300">
          <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
            <img src={learningLogo} alt="Ente AI Learning Platform" className="w-24 h-24 md:w-28 md:h-28 object-contain drop-shadow-lg" />
            <div>
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
                Ente AI Learning Platform
              </h2>
              <p className="text-1xl md:text-2xl e mb-4 font-semibold text-[#D1D1D1]">
                  Build Skills That Matter
                </p>
              <p className="text-blue-100 text-lg max-w-xl">
                Access our comprehensive digital library, track your progress, and join interactive coding sessions on our dedicated learning portal.
              </p>
            </div>
          </div>
          <button 
            onClick={() => window.open("https://learning.enteai.in", "_blank")}
            className="bg-[#D1D1D1] text-[#003049] px-8 py-4 rounded-xl font-bold text-lg hover:bg-white transition-all shadow-lg hover:shadow-2xl whitespace-nowrap"
          >
            Go to Platform →
          </button>
        </div>
      </motion.div>

      {/* Services Section */}
      <section className="px-6 md:px-16 py-16 bg-[#003049]/30 backdrop-blur-sm relative ">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="border border-[#457B9D] rounded-lg shadow-sm bg-[#002639]/90 hover:bg-[#004e75] hover:border-[#D1D1D1] transition flex flex-col items-center text-center group duration-300 hover:-translate-y-2"
              >
                <div className="h-full w-full flex rounded-md mb-4 flex items-center justify-center  transition-colors ">
                    <div className="w-full h-full flex items-center justify-center">
                    {index === 0 && <img className="w-full h-full object-cover rounded-md" src={project} alt="project--v1"/>}
                    {index === 1 && <img className="w-full h-full object-cover rounded-md" src={consultation} alt="consultation"/>}
                    {index === 2 && <img className="w-full h-full object-cover rounded-md" src={classes} alt="robot-2"/>}
                    {index === 3 && <img className="w-full h-full object-cover rounded-md" src={custom} alt="maintenance"/>}

                  </div>
                </div>
                <h3 className="font-semibold text-lg text-white mb-4 group-hover:text-[#D1D1D1] transition-colors">
                  {item.title}
                </h3>
              </motion.div>
            ))}
          </div>
          {/* Right Content */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center md:text-left"
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              Discover Our Services
            </h3>
            <p className="text-gray-300 mb-6">
              We provide custom robot development, AI integration, and expert consulting to help you innovate and excel in robotics. Our team delivers tailored solutions for businesses and individuals, ensuring you stay ahead in technology.
            </p>
            <button 
              className="border-2 border-[#D1D1D1] text-[#D1D1D1] hover:bg-[#D1D1D1] hover:text-[#003049] px-5 py-2 rounded-md font-bold shadow transition-all duration-300"
              onClick={() => window.location.href = "/services"}
            >
              Know More →
            </button>
          </motion.div>
        </div>
      </section>

      {/* Our Environment Section */}
      <section className="px-6 md:px-16 py-16 relative ">
        <motion.div
           initial={{ opacity: 0, y: 20 }}
           whileInView={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.6 }}
           viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-white mb-4 text-center">
            Our Environment
          </h3>
          <p className="text-gray-300 mb-10 text-center max-w-2xl mx-auto">
            Take a look at our company environment and activities to get a sense of our culture and innovation.
          </p>
        </motion.div>

        <div className="overflow-x-auto pb-4">
          <div className="flex gap-7 mb-5 min-w-[900px] pl-4 md:pl-0 md:justify-center">
            {[
              { src: printingImg, title: "3D Printing" },
              { src: roboticsImg, title: "Workshops" },
              { src: aiImg, title: "AI & Coding Hub" },
              { src: droneImg, title: "Aerial & Underwater Drones" },
              { src: exhibitionImg, title: "Exhibition & Product Expo" },
            ].map((item, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="bg-[#002639] rounded-xl shadow-lg border border-[#457B9D] flex flex-col items-center min-w-[270px] h-[340px] overflow-hidden group hover:shadow-[#457B9D]/40 hover:-translate-y-2 transition-all duration-300"
              >
                <div className="bg-[#003049] h-56 w-full mb-4 flex items-center justify-center overflow-hidden">
                  <img 
                    src={item.src} 
                    alt={item.title} 
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-90 group-hover:opacity-100" 
                  />
                </div>
                <div className="font-semibold p-4 text-lg text-white group-hover:text-[#D1D1D1] text-center transition-colors">{item.title}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="px-6 md:px-16 py-16 bg-[#003049]/50 backdrop-blur-sm relative">
        <h3 className="text-2xl font-bold text-white mb-8 text-center">
          Products
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {[
            { name: "E - MOM", desc: "A Companion for your Safety" },
            { name: "SARDINE R1", desc: "Camera-powered Underwater Drone." },
          ].map((device, idx) => (
            <motion.div 
              key={idx} 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="relative bg-[#002639] border border-[#457B9D] rounded-xl shadow-lg p-6 flex flex-col items-center text-center overflow-hidden group"
            >
              <div className="absolute inset-0 bg-[#003049]/80 backdrop-blur-sm rounded-xl flex items-center justify-center transition-opacity group-hover:bg-[#003049]/70 z-10">
                <span className="text-[#D1D1D1] text-lg font-bold uppercase tracking-wider">Coming Soon</span>
              </div>
              <div className="z-0 opacity-50 group-hover:opacity-70 transition-opacity">
                <h4 className="font-semibold text-lg text-white mb-2">{device.name}</h4>
                <p className="text-sm text-gray-400">{device.desc}</p>
              </div>
            </motion.div>
          ))}
          <motion.div 
             initial={{ opacity: 0, scale: 0.95 }}
             whileInView={{ opacity: 1, scale: 1 }}
             transition={{ delay: 0.2 }}
             viewport={{ once: true }}
             className="flex flex-col items-center justify-center bg-[#D1D1D1] hover:bg-[#bfbfbf] rounded-xl shadow-lg p-6 text-center cursor-pointer transition-colors"
          >
            <span className="text-[#003049] text-lg font-bold">Know More</span>
          </motion.div>
        </div>
      </section>

      {/* Our Impact Section */}
      <section className="px-6 md:px-16 py-16 relative ">
        <h3 className="text-2xl font-bold text-white mb-8 text-center">
          Our Impact
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
          {[  
            { label: " Students Trained", end: 300 },
            { label: " Projects Delivered", end: 35 },
            { label: " Workshops", end: 30 },
          ].map((item, idx) => (
            <CountUpCard key={idx} label={item.label} end={item.end} />
          ))}
        </div>
      </section>

      {/* Clients Section */}
      <section className="px-6 md:px-16 py-16 relative  bg-[#003049]/70 backdrop-blur-sm relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h3 className="text-2xl font-bold text-white mb-4">SUPPORTING THE VISION OF</h3>
          
        </motion.div>

        <div className="flex flex-wrap justify-center items-center gap-6 md:gap-16 opacity-70 ">
          {/* Replace src with actual client logo paths */}
          {[
            { name: "Client 1", logo: "https://www.startupindia.gov.in/content/dam/invest-india/newhomepage/Logo1.png "},
            { name: "Client 2", logo: ksumLogo },
            { name: "Client 3", logo: "https://isuremedia.com/wp-content/uploads/2022/07/msme.webp" },
            // { name: "Client 4", logo: "https://www.yuvakshetra.org/wp-content/uploads/2025/01/yuva-logo-new-1.png" },
          ].map((client, idx) => (
            <motion.img
              key={idx}
              src={client.logo}
              alt={client.name}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="h-12 md:h-16 w-auto object-contain hover:opacity-100 transition-all duration-300"
            />
          ))}
        </div>
      </section>


      {/* Call to Action Section */}
      <section className=" text-white text-center py-16 px-6 relative z-10 ">
        <motion.div
           initial={{ opacity: 0, y: 30 }}
           whileInView={{ opacity: 1, y: 0 }}
           transition={{ duration: 0.7 }}
           viewport={{ once: true }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to start your robotics journey?
          </h2>
          <p className="mb-8 max-w-2xl mx-auto text-gray-200">
            Join our community and enhance your skills with our advanced robotics
            courses and services. Sign up now to get started!
          </p>
          <div className="flex justify-center gap-4">
            <button
              className="bg-[#D1D1D1] text-[#003049] px-6 py-3 rounded-md font-bold shadow-lg hover:shadow-[0_0_15px_rgba(209,209,209,0.5)] transition-all"
              onClick={() => window.location.href = "/"}
            >
              Coming Soon
            </button>
          </div>
        </motion.div>
      </section>
      
      {/* Footer */}
      <footer className="bg-[#001824] text-center py-6 text-sm text-gray-400 relative border-t border-[#457B9D]/20">
        © 2025 ENTE AI. All rights reserved.
      </footer>
    </div>
  );
}

export default LandingPage;